import type { PaginatedResponse } from '@/interfaces/paginatedResponse'
import axios from '@/lib/axios'
import { useQuery } from '@tanstack/vue-query'
import { computed, ref, type Ref } from 'vue'

export type MyFeedbackCategory = 'bug' | 'idea' | 'otro'

export type MyFeedbackStatus =
	| 'nuevo'
	| 'en_revision'
	| 'confirmado'
	| 'en_progreso'
	| 'resuelto'
	| 'no_reproducible'
	| 'duplicado'
	| 'descartado'
	| 'planeada'

/**
 * Tres familias y no nueve colores: al usuario le importa si su reporte sigue
 * vivo, si se arregló o si se cerró sin cambio. El backend la manda ya resuelta
 * (`FeedbackStatus::family()`) para que las dos apps no la deriven por separado.
 */
export type MyFeedbackStatusFamily = 'abierto' | 'resuelto' | 'cerrado'

export type MyFeedbackEventType =
	| 'status_change'
	| 'note'
	| 'reply'
	| 'field_change'
	| 'duplicate_linked'
	| 'attachment_removed'

export interface MyFeedbackEvent {
	id: number
	type: MyFeedbackEventType
	body: string | null
	status: MyFeedbackStatus | null
	statusLabel: string | null
	/** "Soporte" o "Tú". El backend nunca manda el nombre de quien atiende. */
	author: string
	isMine: boolean
	createdAt: string
}

export interface MyFeedback {
	id: number
	category: MyFeedbackCategory
	comment: string
	route: string
	testimonialConsent: boolean | null
	status: MyFeedbackStatus
	/** Etiqueta pública, distinta de la que ve soporte en el triage. */
	statusLabel: string
	statusFamily: MyFeedbackStatusFamily
	isClosed: boolean
	/**
	 * Testigo opaco de concurrencia. Se devuelve al responder para que el backend
	 * pueda rechazar la réplica si el reporte cambió mientras se escribía.
	 */
	version: string
	closedAt: string | null
	resolutionNote: string | null
	/** Sólo llega en el detalle: el listado no carga la bitácora de cada fila. */
	timeline?: MyFeedbackEvent[]
	createdAt: string
}

/** `null` = todas las categorías. */
export type CategoryFilter = MyFeedbackCategory | null

/** `null` = todos los estatus. */
export type StatusFilter = MyFeedbackStatus | null

interface Params {
	category: CategoryFilter
	status: StatusFilter
	page: number
	perPage: number
}

const getMyFeedback = async (params: Params): Promise<PaginatedResponse<MyFeedback>> => {
	const { data } = await axios.get<PaginatedResponse<MyFeedback>>('/api/feedback', {
		params: {
			page: params.page,
			per_page: params.perPage,
			...(params.category ? { category: params.category } : {}),
			...(params.status ? { status: params.status } : {}),
		},
	})

	return data
}

/**
 * Feedback que envió el usuario autenticado.
 *
 * El endpoint no recibe id: BookingV2 lo scopea al dueño del token. Por eso este
 * composable sirve únicamente para el perfil propio, no para ver el de otro
 * usuario.
 *
 * Lo que llega ya viene acotado por la ventana de visibilidad del backend: los
 * reportes abiertos siempre, y los cerrados sólo durante 48 horas. La lista no
 * filtra nada de eso del lado del cliente — si lo hiciera, tendría que conocer
 * la ventana y las dos se desincronizarían.
 */
/**
 * Cuántos reportes por página.
 *
 * `DEEP_LINK` es más grande a propósito y sólo se usa cuando se llegó desde la
 * campana. El endpoint ordena por fecha de CREACIÓN, así que un reporte viejo al
 * que soporte le acaba de contestar queda fuera de la primera página — y ese es
 * justamente el reporte del que habla la notificación. Con la página normal, el
 * deep link aterrizaba en la lista y no había tarjeta que abrir.
 *
 * Los valores permitidos los fija `ValidatePaginationParameters` de BookingV2:
 * 5, 10, 15, 25, 50 y 100. Pedir otro devuelve 422.
 */
const PER_PAGE = { NORMAL: 10, DEEP_LINK: 50 } as const

const useMyFeedback = (options: { hasDeepLink?: Ref<boolean> } = {}) => {
	const category: Ref<CategoryFilter> = ref(null)
	const status: Ref<StatusFilter> = ref(null)
	const page = ref(1)

	const perPage = computed(() =>
		options.hasDeepLink?.value ? PER_PAGE.DEEP_LINK : PER_PAGE.NORMAL
	)

	const { data, isLoading, isFetching, isError, refetch } = useQuery(
		['myFeedback', category, status, page, perPage],
		() =>
			getMyFeedback({
				category: category.value,
				status: status.value,
				page: page.value,
				perPage: perPage.value,
			}),
		{ keepPreviousData: true }
	)

	const feedbacks = computed(() => data.value?.data ?? [])
	const meta = computed(() => data.value?.meta)

	function setCategory(value: CategoryFilter) {
		category.value = value
		page.value = 1
	}

	function setStatus(value: StatusFilter) {
		status.value = value
		page.value = 1
	}

	function setPage(value: number) {
		page.value = value
	}

	return {
		feedbacks,
		meta,
		category,
		status,
		page,
		isLoading,
		isFetching,
		isError,

		setCategory,
		setStatus,
		setPage,
		refetch,
	}
}

export default useMyFeedback
